import { createContext, memo, useContext, useId } from 'react';

export type WorkspaceAgentBeforeAfterRow = {
  after: string;
  before: string;
};

export const WORKSPACE_AGENT_BEFORE_AFTER_TABLE_DEFAULT_REGION_LABEL = 'Changes preview';

const ScrollRegionRefContext = createContext<((node: HTMLElement | null) => void) | undefined>(undefined);

export const WorkspaceAgentBeforeAfterTableScrollRegionRefProvider = ScrollRegionRefContext.Provider;

const useWorkspaceAgentBeforeAfterScrollRegionRef = () => useContext(ScrollRegionRefContext);

export type WorkspaceAgentBeforeAfterTableProps = {
  /** A line beneath the table for what it does not show, e.g. `and 12 more`. */
  caption?: string;
  'data-testid'?: string;
  regionLabel?: string;
  rows: WorkspaceAgentBeforeAfterRow[];
};

/**
 * What a rename would do to each file, old name against new.
 *
 * Monospace on both columns so the parts that differ line up vertically — the point of the table is
 * spotting the one row where the pattern went wrong, which proportional text hides. Names wrap rather
 * than truncate for the same reason: a clipped filename is the half you cannot check.
 */
export const WorkspaceAgentBeforeAfterTable = memo(
  ({
    caption,
    'data-testid': testId,
    regionLabel = WORKSPACE_AGENT_BEFORE_AFTER_TABLE_DEFAULT_REGION_LABEL,
    rows,
  }: WorkspaceAgentBeforeAfterTableProps) => {
    const captionId = useId();
    const setScrollRegionRef = useWorkspaceAgentBeforeAfterScrollRegionRef();

    if (!rows.length) {
      return null;
    }

    return (
      <div className={caption ? 'space-y-2' : undefined} data-testid={testId}>
        <section
          aria-label={regionLabel}
          className="max-h-64 overflow-y-auto"
          ref={setScrollRegionRef}
          // biome-ignore lint/a11y/noNoninteractiveTabindex: scrollable region must be focusable for keyboard scrolling
          tabIndex={0}
        >
          <table aria-describedby={caption ? captionId : undefined} className="w-full table-fixed text-left text-12">
            {/* The header needs a background of its own: without one the rows scroll visibly behind it. */}
            <thead className="sticky top-0 bg-gray-1">
              <tr className="border-gray-3 border-b">
                <th className="py-2 pr-3 font-medium text-gray-9 leading-4" scope="col">
                  Before
                </th>
                <th className="py-2 font-medium text-gray-9 leading-4" scope="col">
                  After
                </th>
              </tr>
            </thead>
            <tbody>
              {rows.map((row) => (
                <tr
                  className="border-gray-3 border-b last:border-b-0"
                  data-testid="WORKSPACE_AGENT_BEFORE_AFTER_ROW"
                  key={`${row.before}-${row.after}`}
                >
                  <td
                    className="break-all py-2 pr-3 align-top font-mono text-gray-9 leading-4"
                    data-testid="WORKSPACE_AGENT_BEFORE_AFTER_BEFORE"
                  >
                    {row.before}
                  </td>
                  <td
                    className="break-all py-2 align-top font-mono text-gray-12 leading-4"
                    data-testid="WORKSPACE_AGENT_BEFORE_AFTER_AFTER"
                  >
                    {row.after}
                  </td>
                </tr>
              ))}
            </tbody>
          </table>
        </section>
        {caption ? (
          <p
            className="text-12 text-gray-9 leading-4"
            data-testid="WORKSPACE_AGENT_BEFORE_AFTER_CAPTION"
            id={captionId}
          >
            {caption}
          </p>
        ) : null}
      </div>
    );
  },
);

WorkspaceAgentBeforeAfterTable.displayName = 'WorkspaceAgentBeforeAfterTable';
